import {
  ActionIcon,
  Button,
  Card,
  Center,
  Group,
  SegmentedControl,
  Stack
} from '@mantine/core'
import { UseFormReturnType } from '@mantine/form'
import { IconDeviceFloppy, IconTrash } from '@tabler/icons-react'
import { FormattedMessage, useIntl } from 'react-intl'
import {
  ACTION_TYPE_CONFIG,
  createDefaultAction,
  getActionTypesForPipeline,
  isPatrolReservedTag
} from '@/components/vlm/vlmPipelineConfig'
import { PipelineType, VlmActionType } from '@/types/vlm'
import { ActionAlertTags } from './ActionAlertTags'
import { ActionSummary } from './ActionSummary'
import { EmailActionForm } from './EmailActionForm'
import { MilestoneActionForm } from './MilestoneActionForm'
import { PipelineFormValues } from './VlmPipelineForm'
import { actionHasErrors } from './actionValidation'

type ActionCardProps = {
  form: UseFormReturnType<PipelineFormValues>
  index: number
}

export const ActionCard = ({ form, index }: ActionCardProps) => {
  const intl = useIntl()
  const type = form.values.actions[index].type
  const isEditing = form.values.actions[index].isEditing

  const allowedTypes = getActionTypesForPipeline(
    form.values.pipelineType as PipelineType,
    form.values.source.patrolVideoSource
  )

  const handleTypeChange = (value: string) => {
    const nextType = value as VlmActionType

    if (nextType === type) return

    const { alert_tags, uuid } = form.values.actions[index]

    form.setFieldValue(`actions.${index}`, {
      ...createDefaultAction(nextType),
      uuid,
      // Milestone actions can't fire on the round summary's reserved tags.
      alert_tags:
        nextType === 'milestone'
          ? alert_tags.filter((tag) => !isPatrolReservedTag(tag))
          : alert_tags,
      isEditing: true
    })
  }

  const handleSave = () => {
    if (actionHasErrors(form, index)) return
    form.setFieldValue(`actions.${index}.isEditing`, false)
  }

  const handleEdit = () =>
    form.setFieldValue(`actions.${index}.isEditing`, true)

  const handleDelete = () => form.removeListItem('actions', index)

  if (!isEditing) {
    return (
      <Card padding="md" withBorder>
        <ActionSummary
          form={form}
          index={index}
          onEdit={handleEdit}
          onDelete={handleDelete}
        />
      </Card>
    )
  }

  return (
    <Card padding="md" pos="relative" withBorder>
      <ActionIcon
        color="red"
        variant="subtle"
        pos="absolute"
        top={8}
        right={8}
        aria-label={intl.formatMessage({
          id: 'vlm.pipelines.create.actions.deleteButton'
        })}
        onClick={handleDelete}
      >
        <IconTrash size={16} />
      </ActionIcon>

      <Stack gap="md" pt={4}>
        {allowedTypes.length > 1 && (
          <SegmentedControl
            size="xs"
            w={220}
            value={type}
            data={allowedTypes.map((actionType) => {
              const { icon, labelId } = ACTION_TYPE_CONFIG[actionType]

              return {
                value: actionType,
                label: (
                  <Center style={{ gap: 8 }}>
                    {icon}
                    <FormattedMessage id={labelId} />
                  </Center>
                )
              }
            })}
            onChange={handleTypeChange}
          />
        )}

        <ActionAlertTags form={form} index={index} />

        {type === 'email' && <EmailActionForm form={form} index={index} />}

        {type === 'milestone' && (
          <MilestoneActionForm form={form} index={index} />
        )}

        <Group justify="flex-end">
          <Button
            size="xs"
            leftSection={<IconDeviceFloppy size={14} />}
            onClick={handleSave}
          >
            <FormattedMessage id="vlm.pipelines.create.actions.saveButton" />
          </Button>
        </Group>
      </Stack>
    </Card>
  )
}
